import React, { useEffect, useState, useCallback, useRef } from "react";
import OfficeAlertsButton from "../components/OfficeAlertsButton";
import axios from "axios";
import { Navigate } from "react-router-dom";
import {
  Clock, Utensils, Truck, ShoppingBag, Loader2, ChefHat, CheckCircle2,
  Volume2, VolumeX, MessageSquare, X, Phone, Megaphone
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useOfficeManifest } from "../hooks/useOfficeManifest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 10000;
const MUTE_STORAGE_KEY = "mm_kitchen_muted";
const VOICE_STORAGE_KEY = "mm_kitchen_voice";

/* Spoken announcement for a freshly-landed order — read by OpenAI TTS. */
function orderAnnouncement(order) {
  const items = (order.items || [])
    .map((it) => `${it.quantity} ${it.name || humanItem(it.lookup_key)}`)
    .join(", ");
  const type = (order.order_type || "takeaway").replace(/_/g, " ");
  const table = order.table_number ? ` for table ${order.table_number}` : "";
  const who = order.customer_name ? ` For ${order.customer_name.split(" ")[0]}.` : "";
  const notes = order.notes ? ` Note: ${order.notes}.` : "";
  return `New ${type} order${table}. ${items}.${who}${notes}`.slice(0, 580);
}

function humanItem(key) {
  return (key || "").replace(/^mamamina_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function elapsedFrom(iso, now) {
  if (!iso) return null;
  try {
    return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  } catch {
    return null;
  }
}

const TYPE_ICONS = { dine_in: Utensils, takeaway: ShoppingBag, delivery: Truck };

/* ----------- Web Audio chime — no external asset needed ----------- */
function useChime() {
  const audioCtxRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_STORAGE_KEY) === "1"; }
    catch (err) { console.warn("[kitchen] localStorage read failed", err); return false; }
  });

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_STORAGE_KEY, next ? "1" : "0"); }
      catch (err) { console.warn("[kitchen] localStorage write failed", err); }
      return next;
    });
  };

  const play = useCallback((kind = "overdue") => {
    if (muted) return;
    try {
      if (!audioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = new AC();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Two distinct tone families so mama can tell them apart from
      // across the pass:
      //   • "new"      → bright triple-tap (C6 → E6 → G6, quick)
      //   • "overdue"  → warmer two-note ding (G5 → C6, longer)
      const notes = kind === "new"
        ? [{ f: 1046, t: 0 }, { f: 1318, t: 0.09 }, { f: 1568, t: 0.19 }]
        : [{ f: 784,  t: 0 }, { f: 1046, t: 0.18 }];
      notes.forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = kind === "new" ? "triangle" : "sine";
        osc.frequency.value = f;
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.28, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + (kind === "new" ? 0.28 : 0.5));
        osc.start(now + t);
        osc.stop(now + t + (kind === "new" ? 0.32 : 0.55));
      });
    } catch (err) {
      console.warn("[kitchen] chime failed", err);
    }
  }, [muted]);

  return { play, muted, toggleMute };
}

/* ---------------------- Kitchen Board Page ---------------------- */
export default function Kitchen() {
  const { isAuthenticated, isAdmin, loading, authHeaders } = useAuth();
  // Kitchen page also installs as Mamamina Office (same PWA)
  useOfficeManifest();
  const [board, setBoard] = useState({ orders: [], server_time: null });
  const [today, setToday] = useState({ revenue_aud: 0, orders: 0 });
  const [pulse, setPulse] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const [saving, setSaving] = useState(null);
  const [selected, setSelected] = useState(null);   // timeline modal
  const alertedRef = useRef(new Set());
  const seenOrderIdsRef = useRef(null);   // null on first mount so we don't chime for pre-existing orders
  const { play, muted, toggleMute } = useChime();

  // Voice readout — kitchen speaks each new order aloud after the chime.
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem(VOICE_STORAGE_KEY) !== "0"; }
    catch { return true; }
  });
  const toggleVoice = () => {
    setVoiceOn((v) => {
      const next = !v;
      try { localStorage.setItem(VOICE_STORAGE_KEY, next ? "1" : "0"); }
      catch (err) { console.warn("[kitchen] localStorage write failed", err); }
      return next;
    });
  };
  const speakChainRef = useRef(Promise.resolve());
  const queueSpeak = useCallback((text, delayMs = 0) => {
    speakChainRef.current = speakChainRef.current.then(async () => {
      if (delayMs) await new Promise((r) => setTimeout(r, delayMs));
      try {
        const { data } = await axios.post(`${API}/admin/tts`, { text }, { responseType: "blob" });
        await new Promise((resolve) => {
          const url = URL.createObjectURL(data);
          const audio = new Audio(url);
          const done = () => { URL.revokeObjectURL(url); resolve(); };
          audio.onended = done;
          audio.onerror = done;
          audio.play().catch(done);
        });
      } catch (err) {
        console.warn("[kitchen] voice readout failed", err?.message);
      }
    });
  }, []);

  const fetchBoard = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/admin/kitchen`, { headers: authHeaders }),
        axios.get(`${API}/admin/analytics/today`, { headers: authHeaders }),
      ]);
      setBoard(b.data);
      setToday((prev) => {
        if (t.data.revenue_aud > prev.revenue_aud) {
          setPulse(true);
          setTimeout(() => setPulse(false), 1200);
        }
        return t.data;
      });
    } catch (err) {
      // Auth failure — useAuth redirects handle it. Log for debug.
      console.warn("[kitchen] board fetch failed", err?.response?.status || err?.message);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchBoard();
    const poll = setInterval(fetchBoard, REFRESH_MS);
    const clock = setInterval(() => setTick(Date.now()), 30000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [isAdmin, fetchBoard]);

  // Detect overdue transitions AND newly-landed orders — different chimes
  useEffect(() => {
    const now = Date.now();
    let newOverdue = 0;
    (board.orders || []).forEach((order) => {
      if ((order.fulfillment_status || "") !== "preparing") return;
      if (!order.preparing_at || !order.eta_minutes) return;
      const dueAt = new Date(order.preparing_at).getTime() + order.eta_minutes * 60000;
      if (now >= dueAt && !alertedRef.current.has(order.session_id)) {
        alertedRef.current.add(order.session_id);
        newOverdue += 1;
      }
    });

    // NEW-order detection: bright triple-tap chime whenever a session_id
    // appears that we haven't seen before. First mount = quiet (we don't
    // spam on page load with pre-existing orders).
    const currentIds = new Set((board.orders || []).map((o) => o.session_id));
    const freshOrders = [];
    if (seenOrderIdsRef.current !== null) {
      (board.orders || []).forEach((o) => {
        if (!seenOrderIdsRef.current.has(o.session_id)) freshOrders.push(o);
      });
    }
    seenOrderIdsRef.current = currentIds;

    if (freshOrders.length > 0) {
      play("new");
      if (voiceOn) {
        // Speak each new order in sequence, first one waits for the chime.
        freshOrders.slice(0, 3).forEach((o, i) => queueSpeak(orderAnnouncement(o), i === 0 ? 700 : 250));
      }
    } else if (newOverdue > 0) play("overdue");

    // Clear alertedRef entries for orders no longer visible
    Array.from(alertedRef.current).forEach((sid) => {
      if (!currentIds.has(sid)) alertedRef.current.delete(sid);
    });
  }, [board.orders, play, voiceOn, queueSpeak]);

  const setStatus = async (session_id, status) => {
    setSaving(session_id + status);
    try {
      await axios.patch(`${API}/admin/orders/${session_id}/status`, { status }, { headers: authHeaders });
      await fetchBoard();
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="mm-dark min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={48} className="text-[var(--mm-accent)] animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/account" replace />;

  const now = Date.now();

  return (
    <div className="mm-dark min-h-screen bg-black text-white overflow-x-hidden" data-testid="kitchen-display">
      {/* Header bar */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur border-b-2 border-[var(--mm-accent)] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ChefHat size={32} className="text-[var(--mm-accent)]" />
          <div>
            <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[var(--mm-accent)]">Mamamina&apos;s Soulfeed</div>
            <div className="font-display italic text-2xl md:text-3xl">Kitchen Board</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div
            className={`text-right transition-all ${pulse ? "scale-110" : ""}`}
            data-testid="kitchen-today-revenue"
          >
            <div className="text-[0.7rem] tracking-[0.24em] uppercase text-white/60">Today</div>
            <div className={`font-display text-3xl leading-none ${pulse ? "text-white" : "text-[var(--mm-accent)]"}`}>
              ${today.revenue_aud.toFixed(2)}
            </div>
            <div className="text-[0.62rem] tracking-[0.22em] uppercase text-white/40 mt-1">{today.orders} orders</div>
          </div>
          <div className="w-px h-14 bg-white/10" />
          <OfficeAlertsButton />
          <button
            onClick={toggleMute}
            data-testid="kitchen-mute-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 border-2 transition-all ${
              muted
                ? "border-white/25 text-white/60 hover:border-[var(--mm-accent)]"
                : "border-[var(--mm-accent)] text-[var(--mm-accent)]"
            }`}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            <span className="text-xs tracking-[0.22em] uppercase">{muted ? "Muted" : "Sound On"}</span>
          </button>
          <button
            onClick={toggleVoice}
            data-testid="kitchen-voice-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 border-2 transition-all ${
              voiceOn
                ? "border-[var(--mm-accent)] text-[var(--mm-accent)]"
                : "border-white/25 text-white/60 hover:border-[var(--mm-accent)]"
            }`}
            title="Read new orders aloud"
          >
            <Megaphone size={18} />
            <span className="text-xs tracking-[0.22em] uppercase">{voiceOn ? "Voice On" : "Voice Off"}</span>
          </button>
          {!muted && (
            <button
              onClick={() => play("new")}
              data-testid="kitchen-chime-test"
              className="text-[0.65rem] tracking-[0.22em] uppercase text-white/50 hover:text-[var(--mm-accent)] transition-colors underline underline-offset-4"
              title="Preview the new-order chime"
            >
              Test chime
            </button>
          )}
          {voiceOn && (
            <button
              onClick={() => queueSpeak("Voice check. New takeaway order. 1 Attieke Feast Platter. For Mama.")}
              data-testid="kitchen-voice-test"
              className="text-[0.65rem] tracking-[0.22em] uppercase text-white/50 hover:text-[var(--mm-accent)] transition-colors underline underline-offset-4"
              title="Preview the voice readout"
            >
              Test voice
            </button>
          )}
          <div className="text-right">
            <div className="text-[0.7rem] tracking-[0.24em] uppercase text-white/60">Active</div>
            <div className="font-display text-5xl text-[var(--mm-accent)] leading-none">{board.orders.length}</div>
          </div>
        </div>
      </div>

      {board.orders.length === 0 ? (
        <div className="min-h-[70vh] flex flex-col items-center justify-center text-white/40">
          <CheckCircle2 size={64} className="mb-4 text-[var(--mm-accent)]" />
          <div className="font-display italic text-4xl">All caught up.</div>
          <div className="text-sm tracking-[0.24em] uppercase mt-2">Board refreshes every 10 seconds.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4 md:p-6">
          {board.orders.map((order) => (
            <OrderCard
              key={order.session_id}
              order={order}
              now={now}
              tick={tick}
              saving={saving}
              onSetStatus={setStatus}
              onOpenTimeline={() => setSelected(order)}
            />
          ))}
        </div>
      )}

      {selected && (
        <TimelineModal order={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

/* ---------------------- Order Card ---------------------- */
function OrderCard({ order, now, tick, saving, onSetStatus, onOpenTimeline }) {
  const Icon = TYPE_ICONS[order.order_type] || ShoppingBag;
  const elapsed = elapsedFrom(order.paid_at || order.created_at, now);
  const status = order.fulfillment_status || "new";
  const isPreparing = status === "preparing";
  const isReady = status === "ready";
  const overdue = isPreparing && order.eta_minutes && elapsedFrom(order.preparing_at, now) > order.eta_minutes;
  const borderClass = isReady
    ? "border-[var(--mm-accent)] ring-2 ring-[var(--mm-accent)]/40"
    : overdue
      ? "border-red-500 ring-2 ring-red-500/40 animate-pulse"
      : isPreparing
        ? "border-yellow-500/80"
        : "border-white/20";
  const replies = order.customer_messages || [];

  return (
    <div
      data-testid={`kitchen-order-${order.session_id}`}
      className={`bg-[#0f0f0f] border-2 ${borderClass} p-4 md:p-5 flex flex-col gap-3 shadow-lg transition-all`}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3">
        <button onClick={onOpenTimeline} className="flex items-center gap-3 text-left group" data-testid={`kitchen-timeline-${order.session_id}`}>
          <Icon size={28} className="text-[var(--mm-accent)] shrink-0" />
          <div>
            <div className="font-display italic text-2xl md:text-3xl leading-tight group-hover:underline decoration-[var(--mm-accent)] underline-offset-4">
              {(order.order_type || "takeaway").replace("_", "-").toUpperCase()}
              {order.table_number ? <span className="text-[var(--mm-accent)]"> · T{order.table_number}</span> : ""}
            </div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/60">
              {order.customer_name || "Guest"}
            </div>
          </div>
        </button>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1 text-xs text-white/60">
            <Clock size={12} /> {elapsed}m ago
          </div>
          <div className="text-[0.62rem] tracking-[0.22em] uppercase mt-1">{statusPill(status)}</div>
        </div>
      </div>

      {/* Items */}
      <ul className="text-lg leading-tight space-y-1">
        {(order.items || []).map((it, i) => (
          <li key={`${it.lookup_key || "item"}-${i}`} className="flex items-baseline gap-2">
            <span className="font-display text-[var(--mm-accent)] text-2xl leading-none w-8">×{it.quantity}</span>
            <span>{humanItem(it.lookup_key)}</span>
          </li>
        ))}
      </ul>

      {order.notes && (
        <div className="text-sm bg-yellow-500/10 border-l-4 border-yellow-500 px-3 py-2 whitespace-pre-line">
          <div className="text-[0.62rem] tracking-[0.22em] uppercase text-yellow-500 mb-0.5">Note</div>
          {order.notes}
        </div>
      )}

      {replies.length > 0 && (
        <div className="inline-flex items-center gap-2 text-xs text-[var(--mm-accent)]">
          <MessageSquare size={14} />
          <span className="tracking-[0.18em] uppercase">{replies.length} customer repl{replies.length > 1 ? "ies" : "y"}</span>
        </div>
      )}

      {order.delivery_address && (
        <div className="text-xs text-white/70">
          <span className="tracking-[0.2em] uppercase text-white/40">Ship to </span>
          {order.delivery_address}
        </div>
      )}

      {order.customer_phone && (
        <a
          href={`tel:${order.customer_phone}`}
          data-testid={`kitchen-call-${order.session_id}`}
          className="inline-flex items-center justify-center gap-2 py-2 border border-white/25 text-sm tracking-[0.2em] uppercase text-white/80 hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <Phone size={14} /> Call {order.customer_name?.split(" ")[0] || "Guest"} · {order.customer_phone}
        </a>
      )}

      <div className="grid grid-cols-3 gap-2 mt-1">
        {[
          { key: "preparing", label: "Prepare" },
          { key: "ready", label: "Ready" },
          { key: "completed", label: "Done" }
        ].map((step) => {
          const active = status === step.key;
          return (
            <button
              key={step.key}
              disabled={active || saving === (order.session_id + step.key)}
              onClick={() => onSetStatus(order.session_id, step.key)}
              data-testid={`kitchen-${step.key}-${order.session_id}`}
              className={`text-sm md:text-base font-semibold tracking-[0.2em] uppercase py-3 transition-all border-2 ${
                active
                  ? "bg-[var(--mm-accent)] text-black border-[var(--mm-accent)] cursor-default"
                  : "border-white/25 hover:border-[var(--mm-accent)] hover:bg-[var(--mm-accent)] hover:text-black"
              }`}
            >
              {saving === (order.session_id + step.key) ? "…" : step.label}
            </button>
          );
        })}
      </div>

      {isPreparing && order.eta_minutes && (
        <PreparingCountdown preparingAt={order.preparing_at} etaMinutes={order.eta_minutes} tick={tick} />
      )}
    </div>
  );
}

function statusPill(status) {
  const map = {
    preparing: <span className="px-2 py-0.5 border border-yellow-500 text-yellow-500">Preparing</span>,
    ready: <span className="px-2 py-0.5 border border-[var(--mm-accent)] text-[var(--mm-accent)]">Ready</span>,
    sent_to_pos: <span className="px-2 py-0.5 border border-white/40 text-white/60">Sent</span>,
    new: <span className="px-2 py-0.5 border border-white/40 text-white/70">New</span>
  };
  return map[status] || <span className="px-2 py-0.5 border border-white/40 text-white/60">{status}</span>;
}

function PreparingCountdown({ preparingAt, etaMinutes, tick }) {
  const now = Date.now();
  const start = preparingAt ? new Date(preparingAt).getTime() : null;
  if (!start) return null;
  const elapsedMin = Math.floor((now - start) / 60000);
  const remaining = Math.max(0, etaMinutes - elapsedMin);
  const overdue = remaining === 0 && elapsedMin > etaMinutes;
  return (
    <div className={`text-xs tracking-[0.2em] uppercase ${overdue ? "text-red-400" : "text-white/60"}`} key={tick}>
      {overdue ? "Overdue — should be ready" : `Ready in ~${remaining}m (ETA ${etaMinutes}m)`}
    </div>
  );
}

/* ---------------------- Timeline Modal ---------------------- */
function fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

function durationBetween(a, b) {
  if (!a || !b) return null;
  const secs = Math.max(0, Math.floor((new Date(b).getTime() - new Date(a).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
}

function TimelineModal({ order, onClose }) {
  const stages = [
    { key: "created_at", label: "Placed", value: order.created_at },
    { key: "paid_at", label: "Paid", value: order.paid_at },
    { key: "preparing_at", label: "Started preparing", value: order.preparing_at, eta: order.eta_minutes },
    { key: "ready_at", label: "Marked ready", value: order.ready_at, auto: order.auto_advanced },
    { key: "completed_at", label: "Handed off", value: order.completed_at }
  ].filter((s) => s.value);

  const replies = order.customer_messages || [];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0f0f0f] border-2 border-[var(--mm-accent)] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="kitchen-timeline-modal"
      >
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <div className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--mm-accent)]">Order Timeline</div>
            <div className="font-display italic text-3xl mt-1 text-white">
              {(order.order_type || "takeaway").toUpperCase().replace("_", " ")}
              {order.table_number ? <span className="text-[var(--mm-accent)]"> · T{order.table_number}</span> : ""}
            </div>
            <div className="text-sm text-white/60 mt-1">
              {order.customer_name || "Guest"} · ${((order.amount || 0) / 100).toFixed(2)} · {(order.session_id || "").slice(-10)}
            </div>
            {order.customer_phone && (
              <a
                href={`tel:${order.customer_phone}`}
                data-testid="timeline-call-guest"
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 border border-[var(--mm-accent)] text-[var(--mm-accent)] text-sm tracking-[0.2em] uppercase hover:bg-[var(--mm-accent)] hover:text-black transition-all"
              >
                <Phone size={14} /> Call {order.customer_phone}
              </a>
            )}
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-[var(--mm-accent)]" data-testid="timeline-close">
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Vertical stepper */}
          <ol className="relative border-l-2 border-white/20 pl-5 space-y-5">
            {stages.map((s, idx) => {
              const prev = stages[idx - 1];
              const dur = prev ? durationBetween(prev.value, s.value) : null;
              return (
                <li key={s.key} className="relative">
                  <span className="absolute -left-[27px] top-1 w-4 h-4 rounded-full bg-[var(--mm-accent)] ring-4 ring-black" />
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="text-sm tracking-[0.2em] uppercase text-[var(--mm-accent)]">
                      {s.label}
                      {s.auto && <span className="ml-2 text-white/50 normal-case tracking-normal text-xs">(auto-advanced)</span>}
                      {s.eta && <span className="ml-2 text-white/50 normal-case tracking-normal text-xs">ETA {s.eta}m</span>}
                    </div>
                    <div className="text-xs text-white/60 font-mono">{fmtTime(s.value)}</div>
                  </div>
                  {dur && (
                    <div className="text-xs text-white/50 mt-1">+{dur} since {prev.label.toLowerCase()}</div>
                  )}
                </li>
              );
            })}
          </ol>

          {/* Items */}
          <div>
            <div className="text-[0.62rem] tracking-[0.3em] uppercase text-white/60 mb-2">Items</div>
            <ul className="space-y-1 text-sm">
              {(order.items || []).map((it, i) => (
                <li key={`${it.lookup_key || "item"}-${i}`} className="flex justify-between">
                  <span>×{it.quantity} {humanItem(it.lookup_key)}</span>
                  <span className="text-white/60">${((it.unit_amount || 0) * it.quantity / 100).toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer replies */}
          {replies.length > 0 && (
            <div>
              <div className="text-[0.62rem] tracking-[0.3em] uppercase text-white/60 mb-2">
                Customer SMS replies ({replies.length})
              </div>
              <ul className="space-y-2 text-sm">
                {replies.map((r, i) => (
                  <li key={r.message_sid || r.received_at || `reply-${i}`} className="bg-[var(--mm-accent)]/10 border-l-4 border-[var(--mm-accent)] px-3 py-2">
                    <div className="text-xs text-white/60 mb-0.5">{fmtTime(r.received_at)} · {r.from}</div>
                    <div>{r.body}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
